import React, { useState, useEffect } from 'react';
import { ChevronDown, ArrowRight, Users, Zap, Shield, Star, Check, ExternalLink, Play, Code, Database, Cloud, Smartphone, Globe, Lock, Hospital, Utensils, ChartColumn, Settings, Landmark, Car, ShoppingCart, School } from 'lucide-react';
import Footer from '../layout/Footer';
import Navbar from '../layout/Navbar';
import { Link } from 'react-router-dom';

const Products = () => {
    const [scrollY, setScrollY] = useState(0);
    const [isVisible, setIsVisible] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('all');

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const observers = new Map();
        const elements = document.querySelectorAll('[data-animate]');

        elements.forEach((el, index) => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(prev => ({ ...prev, [index]: true }));
                    }
                },
                { threshold: 0.1 }
            );
            observer.observe(el);
            observers.set(index, observer);
        });

        return () => observers.forEach(observer => observer.disconnect());
    }, []);

    const categories = [
        { id: 'all', name: 'All Products' },
        { id: 'Healthcare', name: 'Healthcare' },
        { id: 'Corporate', name: 'Corporate' },
        { id: 'Restaurants', name: 'Restaurants' },
        { id: 'Finance', name: 'Finance' },
        { id: 'E-Commerce', name: 'E-Commerce' },
        { id: 'Education', name: 'Education' },
        { id: 'Automobile', name: 'Automobile' }
    ];

    const products = [
        {
            id: 1,
            name: 'HIMS (Hospital Information Management System)',
            category: 'Healthcare',
            tagline: 'Smarter healthcare, simplified.',
            description: 'A complete healthcare management solution for hospitals, clinics, and labs to streamline operations and improve patient care.',
            features: ['Patient registration & EMR', 'Appointment & billing management', 'Laboratory & pharmacy integration', 'Reports & analytics dashboard'],
            icon: <Hospital className="w-8 h-8" />,
            // status: 'Featured'
        },
        {
            id: 2,
            name: 'RMS (Restaurant Management System)',
            category: 'Restaurants',
            tagline: 'Run your restaurant with ease.',
            description: 'A smart solution for restaurants and cafes to manage orders, inventory, staff, and billing efficiently.',
            features: ['Table & order management', 'Kitchen display system', 'POS integration', 'Inventory & staff scheduling'],
            icon: <Utensils className="w-8 h-8" />,
            // status: 'Popular'
        },
        {
            id: 3,
            name: 'CRM (Customer Relationship Management)',
            category: 'Corporate',
            tagline: 'Turn relationships into revenue.',
            description: 'A powerful CRM platform to manage customer interactions, leads, and sales pipelines effectively.',
            features: ['Lead & opportunity tracking', 'Automated follow-ups', 'Email/SMS integration', 'Advanced sales reports'],
            icon: <ChartColumn className="w-8 h-8" />,
            // status: 'Enterprise'
        },
        {
            id: 4,
            name: 'HRMS (Human Resource Management System)',
            category: 'Corporate',
            tagline: 'Empowering your workforce digitally.',
            description: 'An advanced HR management system to streamline employee records, payroll, and performance monitoring.',
            features: ['Employee onboarding', 'Payroll & attendance tracking', 'Leave & shift management', 'Performance evaluation tools'],
            icon: <Settings className="w-8 h-8" />,
            // status: 'New'
        },
        {
            id: 5,
            name: 'Pocket Pro',
            category: 'Finance',
            tagline: 'Your smart business companion.',
            description: 'A mobile app designed for professionals to manage schedules, expenses, and productivity on the go.',
            features: ['Expense tracking', 'Task & calendar integration', 'Push notifications', 'Cross-platform support'],
            icon: <Landmark className="w-8 h-8" />,
            // status: 'Critical'
        },
        {
            id: 6,
            name: 'Vehicle Bazaar',
            category: 'Automobile',
            tagline: 'Your trusted vehicle marketplace.',
            description: 'An e-marketplace platform for buying, selling, and leasing vehicles with secure transactions.',
            features: ['Vehicle listing & search', 'Secure payments & verification', 'Dealer & customer dashboard', 'Loan & insurance integration'],
            icon: <Car className="w-8 h-8" />,
            // status: 'Integration'
        },
        {
            id: 7,
            name: 'E-Commerce',
            category: 'E-Commerce',
            tagline: 'Your store, your way.',
            description: 'Custom-built e-commerce solutions for retail businesses to expand online with ease.',
            features: ['Product catalog & cart', 'Payment gateway integration', 'Order & inventory management', 'Responsive UI/UX design'],
            icon: <ShoppingCart className="w-8 h-8" />,
            // status: 'Integration'
        },
        {
            id: 8,
            name: 'Kids Learning',
            category: 'Education',
            tagline: 'Learning made fun & interactive.',
            description: 'Interactive learning solutions for kids with gamified education and parental control features.',
            features: ['Fun quizzes & puzzles', 'Interactive learning modules', 'Parental progress dashboard', 'Safe & child-friendly UI'],
            icon: <School className="w-8 h-8" />,
            // status: 'Integration'
        },
        {
            id: 9,
            name: 'Business Websites',
            category: 'Corporate',
            tagline: 'A strong digital identity for your brand.',
            description: 'Professional websites tailored for businesses, startups, and enterprises to boost online presence.',
            features: ['Responsive modern design', 'SEO optimization', 'Content management system', 'Custom integrations (chat, booking, etc.)'],
            icon: <Globe className="w-8 h-8" />,
            // status: 'Integration'
        },
        {
            id: 10,
            name: 'School Management System',
            category: 'Education',
            tagline: 'Complete digital solution for modern schools.',
            description:
                'A comprehensive School Management System that automates academic, administrative, and communication tasks for schools, making management easier for teachers, students, and parents.',
            features: ['Smart Attendance', 'Fee Management', 'Examination Module', 'Notice Board', 'Student Management', 'Homework', 'Class Master', 'Class wise Division Master', 'Subject Master', 'Assign class Teacher', 'Fee Category Master'],
            icon: <School className="w-8 h-8" />,
        }
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(product => product.category === selectedCategory);

    const getColorClasses = (color) => {
        const colorMap = {
            green: 'from-green-400 to-green-600 text-green-600 border-green-200 bg-green-50',
            blue: 'from-blue-400 to-blue-600 text-blue-600 border-blue-200 bg-blue-50',
            purple: 'from-purple-400 to-purple-600 text-purple-600 border-purple-200 bg-purple-50',
            orange: 'from-orange-400 to-orange-600 text-orange-600 border-orange-200 bg-orange-50',
            red: 'from-red-400 to-red-600 text-red-600 border-red-200 bg-red-50',
            teal: 'from-teal-400 to-teal-600 text-teal-600 border-teal-200 bg-teal-50'
        };
        return colorMap[color] || colorMap.green;
    };

    // const getStatusBadge = (status) => {
    //     const statusMap = {
    //         'Featured': 'bg-green-100 text-green-800',
    //         'Popular': 'bg-blue-100 text-blue-800',
    //         'Enterprise': 'bg-purple-100 text-purple-800',
    //         'New': 'bg-orange-100 text-orange-800',
    //         'Critical': 'bg-red-100 text-red-800',
    //         'Integration': 'bg-teal-100 text-teal-800'
    //     };
    //     return statusMap[status] || statusMap.Featured;
    // };

    return (
        <div className="min-h-screen bg-white text-gray-800">
            <Navbar />
            {/* Hero Section */}
            <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center">
                        <div
                            className="animate-fade-in-up"
                            style={{ transform: `translateY(${scrollY * 0.1}px)` }}
                        >
                            <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
                                Our
                                <br />
                                <span className="text-green-500 inline-block animate-pulse">Products</span>
                            </h1>
                            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
                                Discover our comprehensive suite of <span className="text-green-500 font-semibold">innovative solutions</span> designed to transform your business.
                            </p>
                            {/* <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                                <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2">
                                    <span>Explore Products</span>
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                                <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full text-lg font-semibold hover:border-green-500 hover:text-green-500 transform hover:scale-105 transition-all duration-300 flex items-center space-x-2">
                                    <Play className="w-5 h-5" />
                                    <span>Watch Demo</span>
                                </button>
                            </div> */}
                        </div>

                        {/* Scroll Indicator */}
                        <div className="animate-bounce mt-16">
                            <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-8 bg-white border-b border-gray-200" data-animate>
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="flex flex-wrap justify-center gap-4">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => setSelectedCategory(category.id)}
                                className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${selectedCategory === category.id
                                    ? 'bg-green-500 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Products Grid */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredProducts.map((product, index) => {
                            const colorClasses = getColorClasses(product.color);
                            const [gradientClasses, textColorClass, borderColorClass, bgColorClass] = colorClasses.split(' ');

                            return (
                                <div
                                    key={product.id}
                                    className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200 relative overflow-hidden"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    {/* Status Badge */}
                                    {/* <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(product.status)}`}>
                                        {product.status}
                                    </div> */}

                                    {/* Product Icon */}
                                    {/* <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradientClasses} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {product.icon}
                                    </div> */}
                                    <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-2xl">
                                        {product.icon}
                                    </div>

                                    {/* Product Info */}
                                    <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-green-600 transition-colors duration-300">
                                        {product.name}
                                    </h3>
                                    <p className={`text-sm font-semibold mb-4 ${textColorClass}`}>
                                        {product.tagline}
                                    </p>
                                    <p className="text-gray-600 mb-6 leading-relaxed">
                                        {product.description}
                                    </p>

                                    {/* Features */}
                                    <div className="mb-6">
                                        <h4 className="text-sm font-semibold text-gray-800 mb-3">Key Features:</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            {product.features.map((feature, featureIndex) => (
                                                <div key={featureIndex} className="flex items-center space-x-2">
                                                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                    <span className="text-xs text-gray-600">{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Stats
                                    <div className="flex items-center justify-between mb-6 text-sm">
                                        <div className="flex items-center space-x-1">
                                            <Star className="w-4 h-4 text-yellow-400 fill-current" />
                                            <span className="font-semibold">{product.rating}</span>
                                        </div>
                                        <div className="flex items-center space-x-1 text-gray-600">
                                            <Users className="w-4 h-4" />
                                            <span>{product.clients} clients</span>
                                        </div>
                                    </div> */}

                                    {/* CTA Buttons */}
                                    {/* <div className="flex space-x-3">
                                        <button className={`flex-1 bg-gradient-to-r ${gradientClasses} text-white py-3 px-4 rounded-xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2`}>
                                            <span>Learn More</span>
                                            <ExternalLink className="w-4 h-4" />
                                        </button>
                                        <button className={`border-2 ${borderColorClass} ${textColorClass} py-3 px-4 rounded-xl font-semibold hover:${bgColorClass} transform hover:scale-105 transition-all duration-300`}>
                                            Demo
                                        </button>
                                    </div> */}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Features Overview */}
            <section className="py-20 bg-white px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                            Why Choose <span className="text-green-500">OneHexa?</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our products are built with cutting-edge technology and designed for seamless integration.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                icon: <Zap className="w-8 h-8" />,
                                title: 'Lightning Fast',
                                description: 'Optimized performance with sub-second response times and efficient resource utilization.'
                            },
                            {
                                icon: <Shield className="w-8 h-8" />,
                                title: 'Enterprise Security',
                                description: 'Bank-grade security with end-to-end encryption and compliance certifications.'
                            },
                            {
                                icon: <Code className="w-8 h-8" />,
                                title: 'Easy Integration',
                                description: 'RESTful APIs and comprehensive documentation for seamless system integration.'
                            }
                        ].map((feature, index) => (
                            <div
                                key={index}
                                className="text-center p-8 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-lg transform hover:scale-105 transition-all duration-300 border border-transparent hover:border-green-200"
                            >
                                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-green-600 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 bg-gray-900 px-4 sm:px-6 lg:px-8" data-animate>
                <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Ready to Transform Your <span className="text-green-400">Business?</span>
                    </h2>
                    <p className="text-xl text-gray-300 mb-8">
                        Get started with our products today and experience the OneHexa difference.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/contact">
                            <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2">
                                <span>Start Free Trial</span>
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </Link>
                    </div>
                </div>
            </section>
            <Footer />

            <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out;
        }
      `}</style>
        </div>
    );
};

export default Products;


// import React, { useState, useEffect } from 'react';
// import { ChevronDown, ArrowRight, Users, Zap, Shield, Star, Check, ExternalLink, Play, Code, Database, Cloud, Smartphone, Globe, Lock, Hospital, Utensils, ChartColumn, Settings, Landmark, Car, ShoppingCart, School } from 'lucide-react';
// import Footer from '../layout/Footer';
// import Navbar from '../layout/Navbar';

// const Products = () => {
//     const [scrollY, setScrollY] = useState(0);
//     const [isVisible, setIsVisible] = useState({});
//     const [selectedCategory, setSelectedCategory] = useState('all');

//     useEffect(() => {
//         const handleScroll = () => setScrollY(window.scrollY);
//         window.addEventListener('scroll', handleScroll);
//         return () => window.removeEventListener('scroll', handleScroll);
//     }, []);

//     useEffect(() => {
//         const observers = new Map();
//         const elements = document.querySelectorAll('[data-animate]');

//         elements.forEach((el, index) => {
//             const observer = new IntersectionObserver(
//                 ([entry]) => {
//                     if (entry.isIntersecting) {
//                         setIsVisible(prev => ({ ...prev, [index]: true }));
//                     }
//                 },
//                 { threshold: 0.1 }
//             );
//             observer.observe(el);
//             observers.set(index, observer);
//         });

//         return () => observers.forEach(observer => observer.disconnect());
//     }, []);

//     const categories = [
//         { id: 'all', name: 'All Products' },
//         { id: 'Healthcare', name: 'Healthcare' },
//         { id: 'Corporate', name: 'Corporate' },
//         { id: 'Restaurants', name: 'Restaurants' },
//         { id: 'Finance', name: 'Finance' },
//         { id: 'E-Commerce', name: 'E-Commerce' },
//         { id: 'Education', name: 'Education' },
//         { id: 'Automobile', name: 'Automobile' }
//     ];

//     const products = [
//         {
//             id: 1,
//             name: 'HIMS (Hospital Information Management System)',
//             category: 'Healthcare',
//             tagline: 'Smarter healthcare, simplified.',
//             description: 'A complete healthcare management solution for hospitals, clinics, and labs to streamline operations and improve patient care.',
//             features: ['Patient registration & EMR', 'Appointment & billing management', 'Laboratory & pharmacy integration', 'Reports & analytics dashboard'],
//             icon: <Hospital className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&h=300&fit=crop"
//         },
//         {
//             id: 2,
//             name: 'RMS (Restaurant Management System)',
//             category: 'Restaurants',
//             tagline: 'Run your restaurant with ease.',
//             description: 'A smart solution for restaurants and cafes to manage orders, inventory, staff, and billing efficiently.',
//             features: ['Table & order management', 'Kitchen display system', 'POS integration', 'Inventory & staff scheduling'],
//             icon: <Utensils className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop"
//         },
//         {
//             id: 3,
//             name: 'CRM (Customer Relationship Management)',
//             category: 'Corporate',
//             tagline: 'Turn relationships into revenue.',
//             description: 'A powerful CRM platform to manage customer interactions, leads, and sales pipelines effectively.',
//             features: ['Lead & opportunity tracking', 'Automated follow-ups', 'Email/SMS integration', 'Advanced sales reports'],
//             icon: <ChartColumn className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&h=300&fit=crop"
//         },
//         {
//             id: 4,
//             name: 'HRMS (Human Resource Management System)',
//             category: 'Corporate',
//             tagline: 'Empowering your workforce digitally.',
//             description: 'An advanced HR management system to streamline employee records, payroll, and performance monitoring.',
//             features: ['Employee onboarding', 'Payroll & attendance tracking', 'Leave & shift management', 'Performance evaluation tools'],
//             icon: <Settings className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop"
//         },
//         {
//             id: 5,
//             name: 'Pocket Pro',
//             category: 'Finance',
//             tagline: 'Your smart business companion.',
//             description: 'A mobile app designed for professionals to manage schedules, expenses, and productivity on the go.',
//             features: ['Expense tracking', 'Task & calendar integration', 'Push notifications', 'Cross-platform support'],
//             icon: <Landmark className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop"
//         },
//         {
//             id: 6,
//             name: 'Vehicle Bazaar',
//             category: 'Automobile',
//             tagline: 'Your trusted vehicle marketplace.',
//             description: 'An e-marketplace platform for buying, selling, and leasing vehicles with secure transactions.',
//             features: ['Vehicle listing & search', 'Secure payments & verification', 'Dealer & customer dashboard', 'Loan & insurance integration'],
//             icon: <Car className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=400&h=300&fit=crop"
//         },
//         {
//             id: 7,
//             name: 'E-Commerce',
//             category: 'E-Commerce',
//             tagline: 'Your store, your way.',
//             description: 'Custom-built e-commerce solutions for retail businesses to expand online with ease.',
//             features: ['Product catalog & cart', 'Payment gateway integration', 'Order & inventory management', 'Responsive UI/UX design'],
//             icon: <ShoppingCart className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=400&h=300&fit=crop"
//         },
//         {
//             id: 8,
//             name: 'Kids Learning',
//             category: 'Education',
//             tagline: 'Learning made fun & interactive.',
//             description: 'Interactive learning solutions for kids with gamified education and parental control features.',
//             features: ['Fun quizzes & puzzles', 'Interactive learning modules', 'Parental progress dashboard', 'Safe & child-friendly UI'],
//             icon: <School className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop"
//         },
//         {
//             id: 9,
//             name: 'Business Websites',
//             category: 'Corporate',
//             tagline: 'A strong digital identity for your brand.',
//             description: 'Professional websites tailored for businesses, startups, and enterprises to boost online presence.',
//             features: ['Responsive modern design', 'SEO optimization', 'Content management system', 'Custom integrations (chat, booking, etc.)'],
//             icon: <Globe className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop"
//         },
//         {
//             id: 10,
//             name: 'School Management System',
//             category: 'Education',
//             tagline: 'Complete digital solution for modern schools.',
//             description:
//                 'A comprehensive School Management System that automates academic, administrative, and communication tasks for schools, making management easier for teachers, students, and parents.',
//             features: ['Smart Attendance', 'Fee Management', 'Examination Module', 'Notice Board', 'Student Management', 'Homework', 'Class Master', 'Class wise Division Master', 'Subject Master', 'Assign class Teacher', 'Fee Category Master'],
//             icon: <School className="w-8 h-8" />,
//             image: "https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&h=300&fit=crop"
//         }
//     ];

//     const filteredProducts = selectedCategory === 'all'
//         ? products
//         : products.filter(product => product.category === selectedCategory);

//     return (
//         <div className="min-h-screen bg-white text-gray-800">
//             <Navbar />
            
//             {/* Hero Section with Background Image */}
//             <section className="relative pt-20 pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
//                 <div className="absolute inset-0 z-0">
//                     <img 
//                         src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=1920&h=1080&fit=crop" 
//                         alt="Products Technology" 
//                         className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/50 to-white"></div>
//                 </div>

//                 <div className="relative z-10 max-w-7xl mx-auto">
//                     <div className="text-center">
//                         <div
//                             className="animate-fade-in-up"
//                             style={{ transform: `translateY(${scrollY * 0.1}px)` }}
//                         >
//                             <h1 className="text-5xl md:text-7xl font-bold mt-16 text-gray-800 leading-tight">
//                                 Our
//                                 <br />
//                                 <span className="text-green-500 inline-block animate-pulse">Products</span>
//                             </h1>
//                             <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed capitalize">
//                                 Discover our comprehensive suite of <span className="text-green-500 font-semibold">innovative solutions</span> designed to transform your business.
//                             </p>
//                         </div>

//                         {/* Scroll Indicator */}
//                         <div className="animate-bounce mt-16">
//                             <ChevronDown className="w-8 h-8 text-green-500 mx-auto" />
//                         </div>
//                     </div>
//                 </div>
//             </section>

//             {/* Category Filter */}
//             <section className="py-8 bg-white border-b border-gray-200" data-animate>
//                 <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible[0] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="flex flex-wrap justify-center gap-4">
//                         {categories.map((category) => (
//                             <button
//                                 key={category.id}
//                                 onClick={() => setSelectedCategory(category.id)}
//                                 className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 ${selectedCategory === category.id
//                                     ? 'bg-green-500 text-white shadow-lg'
//                                     : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
//                                     }`}
//                             >
//                                 {category.name}
//                             </button>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* Products Grid */}
//             <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-animate>
//                 <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-200 ${isVisible[1] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//                         {filteredProducts.map((product, index) => (
//                             <div
//                                 key={product.id}
//                                 className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-300 border border-gray-100 group hover:border-green-200 overflow-hidden"
//                                 style={{ animationDelay: `${index * 100}ms` }}
//                             >
//                                 {/* Product Image */}
//                                 <div className="relative h-48 overflow-hidden">
//                                     <img 
//                                         src={product.image} 
//                                         alt={product.name}
//                                         className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
//                                     />
//                                     <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
                                    
//                                     {/* Product Icon Overlay */}
//                                     <div className="absolute bottom-4 left-4 w-12 h-12 flex items-center justify-center bg-white/90 backdrop-blur-sm rounded-xl text-green-600">
//                                         {product.icon}
//                                     </div>
//                                 </div>

//                                 <div className="p-8">
//                                     {/* Product Info */}
//                                     <h3 className="text-2xl font-bold text-gray-800 mb-2 group-hover:text-green-600 transition-colors duration-300">
//                                         {product.name}
//                                     </h3>
//                                     <p className="text-sm font-semibold mb-4 text-green-600">
//                                         {product.tagline}
//                                     </p>
//                                     <p className="text-gray-600 mb-6 leading-relaxed">
//                                         {product.description}
//                                     </p>

//                                     {/* Features */}
//                                     <div className="mb-6">
//                                         <h4 className="text-sm font-semibold text-gray-800 mb-3">Key Features:</h4>
//                                         <div className="grid grid-cols-2 gap-2">
//                                             {product.features.slice(0, 4).map((feature, featureIndex) => (
//                                                 <div key={featureIndex} className="flex items-center space-x-2">
//                                                     <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
//                                                     <span className="text-xs text-gray-600">{feature}</span>
//                                                 </div>
//                                             ))}
//                                         </div>
//                                     </div>
//                                 </div>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* Features Overview */}
//             <section className="py-20 bg-white px-4 sm:px-6 lg:px-8" data-animate>
//                 <div className={`max-w-7xl mx-auto transition-all duration-1000 delay-300 ${isVisible[2] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <div className="text-center mb-16">
//                         <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
//                             Why Choose <span className="text-green-500">OneHexa?</span>
//                         </h2>
//                         <p className="text-xl text-gray-600 max-w-3xl mx-auto">
//                             Our products are built with cutting-edge technology and designed for seamless integration.
//                         </p>
//                     </div>

//                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
//                         {[
//                             {
//                                 icon: <Zap className="w-8 h-8" />,
//                                 title: 'Lightning Fast',
//                                 description: 'Optimized performance with sub-second response times and efficient resource utilization.',
//                                 img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop"
//                             },
//                             {
//                                 icon: <Shield className="w-8 h-8" />,
//                                 title: 'Enterprise Security',
//                                 description: 'Bank-grade security with end-to-end encryption and compliance certifications.',
//                                 img: "https://images.unsplash.com/photo-1563986768609-322da13575f3?w=400&h=300&fit=crop"
//                             },
//                             {
//                                 icon: <Code className="w-8 h-8" />,
//                                 title: 'Easy Integration',
//                                 description: 'RESTful APIs and comprehensive documentation for seamless system integration.',
//                                 img: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=300&fit=crop"
//                             }
//                         ].map((feature, index) => (
//                             <div
//                                 key={index}
//                                 className="rounded-2xl bg-gray-50 hover:bg-white hover:shadow-lg transform hover:scale-105 transition-all duration-300 border border-transparent hover:border-green-200 overflow-hidden group"
//                             >
//                                 <div className="relative h-48 overflow-hidden">
//                                     <img 
//                                         src={feature.img} 
//                                         alt={feature.title}
//                                         className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
//                                     />
//                                     <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent"></div>
//                                 </div>
//                                 <div className="p-8 text-center">
//                                     <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-green-600 mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
//                                         {feature.icon}
//                                     </div>
//                                     <h3 className="text-xl font-bold text-gray-800 mb-4">{feature.title}</h3>
//                                     <p className="text-gray-600 leading-relaxed">{feature.description}</p>
//                                 </div>
//                             </div>
//                         ))}
//                     </div>
//                 </div>
//             </section>

//             {/* CTA Section with Background */}
//             <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden" data-animate>
//                 <div className="absolute inset-0 z-0">
//                     <img 
//                         src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1920&h=600&fit=crop" 
//                         alt="Team Collaboration" 
//                         className="w-full h-full object-cover"
//                     />
//                     <div className="absolute inset-0 bg-gray-900/85"></div>
//                 </div>

//                 <div className={`relative z-10 max-w-4xl mx-auto text-center transition-all duration-1000 delay-400 ${isVisible[3] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
//                     <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
//                         Ready to Transform Your <span className="text-green-400">Business?</span>
//                     </h2>
//                     <p className="text-xl text-gray-300 mb-8">
//                         Get started with our products today and experience the OneHexa difference.
//                     </p>
//                     <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
//                         <a href="/contact">
//                             <button className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2">
//                                 <span>Start Free Trial</span>
//                                 <ArrowRight className="w-5 h-5" />
//                             </button>
//                         </a>
//                     </div>
//                 </div>
//             </section>
            
//             <Footer />

//             <style jsx>{`
//         @keyframes fade-in-up {
//           from {
//             opacity: 0;
//             transform: translateY(30px);
//           }
//           to {
//             opacity: 1;
//             transform: translateY(0);
//           }
//         }
        
//         .animate-fade-in-up {
//           animation: fade-in-up 1s ease-out;
//         }
//       `}</style>
//         </div>
//     );
// };

// export default Products;